import { KeyRound, ShieldCheck, UsersRound, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const FEATURES: { key: string; icon: LucideIcon }[] = [
  { key: 'customers', icon: UsersRound },
  { key: 'licensing', icon: KeyRound },
  { key: 'security', icon: ShieldCheck },
]

export function BrandLogo({ size = 'lg' }: { size?: 'sm' | 'lg' }) {
  const { t } = useTranslation()
  return (
    <span
      className={
        size === 'lg'
          ? 'relative flex size-16 shrink-0 items-center justify-center rounded-2xl bg-white p-2 shadow-[0_8px_24px_-6px_color-mix(in_oklab,var(--black)_45%,transparent)] ring-4 ring-white/10'
          : 'flex size-11 shrink-0 items-center justify-center rounded-xl bg-white p-1.5 shadow-sm ring-1 ring-border'
      }
    >
      <img src="/urunver.png" alt={t('app.name')} className="size-full object-contain" />
    </span>
  )
}

export function BrandPanel() {
  const { t } = useTranslation()
  return (
    <div className="relative hidden overflow-hidden bg-gradient-to-br from-brand-navy via-brand-navy to-brand-blue text-white lg:flex lg:flex-col">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 -top-24 size-80 rounded-full bg-brand-sky/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-16 size-96 rounded-full bg-brand-orange/25 blur-3xl" />
        <div className="absolute inset-0 opacity-60 [background-image:radial-gradient(color-mix(in_oklab,var(--white)_14%,transparent)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:linear-gradient(to_bottom,black,transparent_85%)]" />
        <div className="absolute -bottom-40 -right-40 size-[34rem] rounded-full border border-white/10" />
        <div className="absolute -bottom-24 -right-24 size-[26rem] rounded-full border border-white/10" />
        <img src="/urunver.png" alt="" className="absolute -bottom-16 -right-16 size-80 rotate-[-8deg] opacity-[0.09]" />
      </div>

      <div className="relative flex flex-1 flex-col p-10 xl:p-12">
        <div className="flex items-center gap-4">
          <BrandLogo />
          <div className="min-w-0 leading-tight">
            <p className="text-2xl font-bold tracking-tight">{t('app.name')}</p>
            <p className="mt-0.5 text-sm font-medium text-brand-sky">{t('auth.brand.subtitle')}</p>
          </div>
        </div>

        <div className="mt-auto max-w-md space-y-4 pt-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium backdrop-blur-sm">
            <span className="size-1.5 rounded-full bg-brand-orange shadow-[0_0_0_3px_color-mix(in_oklab,var(--brand-orange)_25%,transparent)]" />
            {t('auth.brand.badge')}
          </span>
          <h2 className="text-3xl font-semibold leading-tight tracking-tight xl:text-4xl">{t('auth.brand.headline')}</h2>
          <p className="text-[15px] leading-relaxed text-white/75">{t('auth.brand.description')}</p>
        </div>

        <ul className="mt-8 grid gap-3">
          {FEATURES.map(({ key, icon: Icon }) => (
            <li key={key} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-3 backdrop-blur-sm">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-brand-sky ring-1 ring-inset ring-white/15">
                <Icon className="size-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{t(`auth.brand.features.${key}.title`)}</p>
                <p className="truncate text-xs text-white/65">{t(`auth.brand.features.${key}.body`)}</p>
              </div>
            </li>
          ))}
        </ul>

        <p className="mt-8 text-xs text-white/50">{t('auth.brand.copyright', { year: new Date().getFullYear() })}</p>
      </div>
    </div>
  )
}
